import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Switch,
  Text,
  Icon,
  useColorModeValue,
  Tooltip,
  Select,
} from '@chakra-ui/react';
import { BarChart3, Lightbulb, Bug, Bot } from 'lucide-react';
import { ChatSettings as ChatSettingsType, AI_PROVIDERS, AIProvider } from '../../types/chat';

interface ChatSettingsProps {
  settings: ChatSettingsType;
  // eslint-disable-next-line no-unused-vars
  onChange: (settings: ChatSettingsType) => void;
  compact?: boolean;
}

export const ChatSettings: React.FC<ChatSettingsProps> = ({
  settings,
  onChange,
  compact = false,
}) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleToggle = (key: keyof ChatSettingsType) => {
    onChange({
      ...settings,
      [key]: !settings[key],
    });
  };

  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...settings,
      ai_provider: e.target.value as AIProvider,
    });
  };

  const settingsItems = [
    {
      key: 'incluir_graficos' as const,
      icon: BarChart3,
      label: 'Gráficos',
      tooltip: 'Gera gráficos visuais quando apropriado',
      color: 'blue.500',
      colorScheme: 'blue',
    },
    {
      key: 'incluir_insights' as const,
      icon: Lightbulb,
      label: 'Insights',
      tooltip: 'Gera insights analíticos sobre os dados',
      color: 'yellow.500',
      colorScheme: 'yellow',
    },
    {
      key: 'debug_mode' as const,
      icon: Bug,
      label: 'Debug',
      tooltip: 'Mostra SQL executado e dados brutos',
      color: 'red.500',
      colorScheme: 'red',
    },
  ];

  if (compact) {
    // Layout horizontal compacto
    return (
      <HStack spacing={4} justify="flex-start" flexWrap="wrap" align="center">
        {/* Seletor de Modelo IA */}
        <Tooltip label="Escolha o modelo de IA" placement="top">
          <HStack spacing={2} minW="fit-content">
            <Icon as={Bot} boxSize={4} color="purple.500" />
            <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
              Modelo:
            </Text>
            <Select
              value={settings.ai_provider}
              onChange={handleProviderChange}
              size="xs"
              width="130px"
              borderRadius="md"
              bg="white"
              fontWeight="medium"
            >
              {AI_PROVIDERS.map(provider => (
                <option key={provider.value} value={provider.value}>
                  {provider.label}
                </option>
              ))}
            </Select>
          </HStack>
        </Tooltip>

        {settingsItems.map(item => (
          <Tooltip key={item.key} label={item.tooltip} placement="top">
            <HStack spacing={2} minW="fit-content">
              <Icon as={item.icon} boxSize={4} color={item.color} />
              <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
                {item.label}
              </Text>
              <Switch
                isChecked={settings[item.key]}
                onChange={() => handleToggle(item.key)}
                colorScheme={item.colorScheme}
                size="sm"
              />
            </HStack>
          </Tooltip>
        ))}
      </HStack>
    );
  }

  // Layout vertical (padrão)
  return (
    <Box bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4} mb={3}>
      <VStack align="stretch" spacing={3}>
        <Text fontWeight="bold" fontSize="sm" color="gray.500" mb={1}>
          CONFIGURAÇÕES DO CHAT
        </Text>

        {/* Seletor de Modelo IA */}
        <HStack justify="space-between">
          <HStack spacing={2}>
            <Icon as={Bot} boxSize={4} color="purple.500" />
            <Tooltip label="Escolha o modelo de IA" placement="top">
              <Text fontSize="sm">Modelo IA</Text>
            </Tooltip>
          </HStack>
          <Select
            value={settings.ai_provider}
            onChange={handleProviderChange}
            size="sm"
            width="150px"
            borderRadius="md"
          >
            {AI_PROVIDERS.map(provider => (
              <option key={provider.value} value={provider.value}>
                {provider.label}
              </option>
            ))}
          </Select>
        </HStack>

        {settingsItems.map(item => (
          <HStack key={item.key} justify="space-between">
            <HStack spacing={2}>
              <Icon as={item.icon} boxSize={4} color={item.color} />
              <Tooltip label={item.tooltip} placement="top">
                <Text fontSize="sm">{item.label}</Text>
              </Tooltip>
            </HStack>
            <Switch
              isChecked={settings[item.key]}
              onChange={() => handleToggle(item.key)}
              colorScheme={item.colorScheme}
              size="sm"
            />
          </HStack>
        ))}
      </VStack>
    </Box>
  );
};
